import { useMemo, useState } from 'react';
import { IconChevSmall, IconPlaySmall } from '../icons.js';
import { ACCENT_DISPLAY, type CoachAccentKey, type CoachCardModel } from '../coachCatalog.js';
import type { AudioBtnState } from '../../../lib/audioBlobPlayer.js';

// ─── Avatar "vinilo" con sheen diagonal ───────────────────────────────────
// Exportado para reusarlo en el rail de coach de la conversación de escritorio.
export function CoachAvatar({ coach, size = 56, glossy = true }: { coach: CoachCardModel; size?: number; glossy?: boolean }) {
  const bg = glossy
    ? `radial-gradient(circle at 30% 28%, ${coach.color}ee 0%, ${coach.color} 45%, ${coach.color}c0 100%)`
    : coach.color;
  return (
    <span className="coach-avatar" style={{ width: size, height: size, background: bg }} aria-hidden="true">
      <span className="coach-avatar-initial" style={{ fontSize: Math.round(size * 0.42), lineHeight: 1 }}>
        {coach.initial}
      </span>
      {glossy && (
        <span
          className="coach-avatar-gloss"
          style={{ width: size * 0.55, height: size * 0.28, top: size * 0.08, left: size * 0.18 }}
        />
      )}
    </span>
  );
}

// ─── Waveform estática con seed por coach (firma única) ────────────────────
function CoachWave({ seed = 1, playing = false }: { seed?: number; playing?: boolean }) {
  const bars = useMemo(() => {
    const out: number[] = [];
    let s = seed * 9301;
    for (let i = 0; i < 28; i++) {
      s = (s * 9301 + 49297) % 233280;
      out.push(0.22 + (s / 233280) * 0.78);
    }
    return out;
  }, [seed]);
  return (
    <span className={`coach-wave ${playing ? 'is-playing' : ''}`} aria-hidden="true">
      {bars.map((h, i) => (
        <span
          key={i}
          className="coach-wave-bar"
          style={{ height: `${Math.round(h * 100)}%`, background: 'currentColor', animationDelay: `${(i * 60) % 1800}ms` }}
        />
      ))}
    </span>
  );
}

interface CoachPickerProps {
  coaches: ReadonlyArray<CoachCardModel>;
  selectedId: string | null;
  loading: boolean;
  error: string | null;
  previewState: AudioBtnState;
  previewingId: string | null;
  onPick: (id: string) => void;
  onPreview: (id: string) => void;
  /**
   * Acento de «Tu inglés»: el filtro arranca en él y una nota avisa que elegir
   * un coach de otro acento cambia la variedad (lo hace el caller en `onPick`).
   */
  varietyAccent?: CoachAccentKey;
  /** Nombre visible de «Tu inglés» para la nota ("Reino Unido"). */
  varietyLabel?: string;
}

/**
 * Selector de coach (deck destacado + grid de tiles). Se adapta al N real de
 * personas de inglés del backend; no asume 10 voces. Cada voz muestra nombre
 * humano + género ("Mujer"/"Hombre") + descriptor; el id del provider queda
 * interno. Elegir una voz (tile o flechas) ya reproduce su demo
 * (`useCoachPicker.select`); el botón "Probar voz" queda como play/pausa.
 *
 * Con voces de más de un acento aparece una fila de chips (Todas / Americano /
 * Británico / …) que filtra los tiles y el ciclo de las flechas. El filtro es
 * sólo de navegación: no cambia la voz elegida. Arranca en el acento de «Tu
 * inglés» (`varietyAccent`); elegir un coach de otro acento cambia la variedad,
 * cosa que hace el caller en `onPick` (encargo 111).
 */
export function CoachPicker({
  coaches,
  selectedId,
  loading,
  error,
  previewState,
  previewingId,
  onPick,
  onPreview,
  varietyAccent,
  varietyLabel,
}: CoachPickerProps) {
  const accents = useMemo(() => [...new Set(coaches.map((c) => c.accentKey))], [coaches]);
  const [accentFilter, setAccentFilter] = useState<CoachAccentKey | null>(varietyAccent ?? null);
  // Un filtro sin coaches (p. ej. un acento que el provider activo no tiene) no
  // deja la grilla vacía: se ven todas.
  const activeFilter = accentFilter !== null && accents.includes(accentFilter) ? accentFilter : null;

  if (loading) {
    return (
      <div className="coach-picker" style={{ padding: '8px 0', color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
        Cargando voces<span className="dots-anim" />
      </div>
    );
  }
  if (error || coaches.length === 0) {
    return (
      <div className="coach-picker" style={{ padding: '8px 0', color: 'var(--text-faint)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5 }}>
        {error ?? 'No hay voces disponibles por ahora.'}
      </div>
    );
  }

  const idx = Math.max(0, coaches.findIndex((c) => c.id === selectedId));
  const coach = coaches[idx] ?? coaches[0]!;
  const playing = previewState === 'playing' && previewingId === coach.id;

  const visible = activeFilter ? coaches.filter((c) => c.accentKey === activeFilter) : coaches;
  // El contador cuenta dentro de lo que se ve (con el filtro de «Tu inglés»
  // puesto, «1 / 2» y no «07 / 10»); si el coach elegido quedó fuera del
  // filtro, cuenta sobre todos. Sin ceros de relleno: a 390 px la fila
  // contador · acento · género no entraba.
  const inVisible = visible.findIndex((c) => c.id === coach.id);
  const countList = inVisible === -1 ? coaches : visible;
  const position = (inVisible === -1 ? idx : inVisible) + 1;
  const total = countList.length;
  const step = (delta: number): void => {
    const pos = visible.findIndex((c) => c.id === coach.id);
    // La voz elegida puede no estar en el filtro: las flechas arrancan del borde.
    const from = pos === -1 ? (delta > 0 ? -1 : 0) : pos;
    onPick(visible[(from + delta + visible.length) % visible.length]!.id);
  };
  const goPrev = (): void => step(-1);
  const goNext = (): void => step(1);

  return (
    <div className="coach-picker">
      <div
        className="coach-deck"
        style={
          {
            '--coach-tint': coach.color,
            '--coach-tint-soft': `${coach.color}22`,
            '--coach-tint-edge': `${coach.color}55`,
          } as React.CSSProperties
        }
      >
        <button className="coach-deck-arrow" onClick={goPrev} aria-label="Coach anterior">
          <IconChevSmall size={18} dir="left" />
        </button>

        <div className="coach-deck-main">
          <div className="coach-deck-avatar-wrap">
            <CoachAvatar coach={coach} size={68} />
            <span className="coach-deck-avatar-flag" aria-hidden="true">
              {coach.flag}
            </span>
          </div>
          <div className="coach-deck-body">
            <div className="coach-deck-meta">
              <span className="coach-deck-idx">
                {position}
                <span className="coach-deck-idx-sep">/</span>
                {total}
              </span>
              <span className="coach-deck-meta-dot" />
              {/* El acento va en palabras ("Británico") en el chip: con varios
                  acentos es lo que distingue voces; el EN-XX sólo repetía la
                  bandera. En mobile la fila no entra con tag + región + género. */}
              <span className="coach-deck-tag coach-deck-accent">{coach.region}</span>
              {coach.genderLabel && (
                <>
                  <span className="coach-deck-meta-dot" />
                  <span className="coach-deck-gender">{coach.genderLabel}</span>
                </>
              )}
            </div>
            <div className="coach-deck-name-row">
              <span className="script coach-deck-name">{coach.name}</span>
            </div>
            <div className="coach-deck-blurb">{coach.blurb}</div>
          </div>
        </div>

        <button className="coach-deck-arrow" onClick={goNext} aria-label="Coach siguiente">
          <IconChevSmall size={18} dir="right" />
        </button>

        <button
          className={`coach-deck-preview ${playing ? 'is-playing' : ''}`}
          onClick={() => onPreview(coach.id)}
          aria-label={playing ? 'Pausar muestra' : 'Probar voz'}
        >
          <span className="coach-deck-preview-icon">
            {playing ? (
              <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor">
                <rect x="2" y="2" width="3" height="8" rx="1" />
                <rect x="7" y="2" width="3" height="8" rx="1" />
              </svg>
            ) : (
              <IconPlaySmall size={10} />
            )}
          </span>
          <span className="coach-deck-preview-label">
            {previewState === 'loading' && previewingId === coach.id
              ? 'Cargando…'
              : playing
                ? 'Reproduciendo muestra…'
                : 'Probar voz'}
          </span>
          <CoachWave seed={idx + 3} playing={playing} />
        </button>
      </div>

      {accents.length > 1 && (
        <div className="coach-accent-chips" role="group" aria-label="Filtrar voces por acento">
          <button
            className={`coach-accent-chip ${activeFilter === null ? 'is-active' : ''}`}
            onClick={() => setAccentFilter(null)}
            aria-pressed={activeFilter === null}
          >
            Todas
          </button>
          {accents.map((a) => (
            <button
              key={a}
              className={`coach-accent-chip ${activeFilter === a ? 'is-active' : ''}`}
              onClick={() => setAccentFilter(a)}
              aria-pressed={activeFilter === a}
            >
              <span aria-hidden="true">{ACCENT_DISPLAY[a].flag}</span> {ACCENT_DISPLAY[a].region}
            </button>
          ))}
        </div>
      )}

      {varietyAccent && varietyLabel && accents.length > 1 && (
        <div className="cc-autonote coach-variety-note">
          <span>
            Tu inglés: {varietyLabel}. Si elegís un coach de otro acento, tu inglés cambia con él.
          </span>
        </div>
      )}

      <div className="coach-tiles">
        {visible.map((c) => {
          const active = c.id === coach.id;
          return (
            <button
              key={c.id}
              className={`coach-tile ${active ? 'is-active' : ''}`}
              onClick={() => onPick(c.id)}
              style={{ '--tile-tint': c.color } as React.CSSProperties}
              title={[c.name, c.region, c.genderLabel, c.blurb].filter(Boolean).join(' · ')}
              aria-label={[c.name, c.region, c.genderLabel].filter(Boolean).join(', ')}
              aria-pressed={active}
            >
              <span className="coach-tile-avatar-wrap">
                <CoachAvatar coach={c} size={32} glossy={false} />
                <span className="coach-tile-flag" aria-hidden="true">
                  {c.flag}
                </span>
              </span>
              <span className="coach-tile-name">{c.name}</span>
              {c.genderLabel && <span className="coach-tile-gender">{c.genderLabel}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
